import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ApiService } from 'src/app/services/api.service';
import { ApiUrl } from 'src/app/services/apiUrl';
import { ToastrService } from 'ngx-toastr';
import { NgxSpinnerService } from 'ngx-spinner';
import { Location } from '@angular/common';
import { NbDialogService, NbDialogRef } from '@nebular/theme';
import * as Swal from 'sweetalert2';
import { normalizeCardUid } from 'src/app/shared/utils/card-uid.util';

@Component({
  selector: 'app-user-cards',
  templateUrl: './user-cards.component.html',
  styleUrls: ['./user-cards.component.scss']
})
export class UserCardsComponent implements OnInit {
  @ViewChild('addCardTemplate') addCardTemplate!: TemplateRef<any>;
  @ViewChild('editCardTemplate') editCardTemplate!: TemplateRef<any>;

  // User data
  userId: string = '';
  userName: string = '';

  // Card data
  userCards: any[] = [];

  // Status flags
  loading: boolean = false;
  error: string | undefined = undefined;

  // Stores for dropdowns
  stores: any[] = [];
  storesLoading: boolean = false;

  // Add card dialog state
  newCardUID: string = '';
  newCardStoreId: string = '';
  addCardSubmitting: boolean = false;
  addCardDialogRef: NbDialogRef<any> | null = null;

  // Edit card dialog state
  editCardUID: string = '';
  editCardStoreId: string = '';
  editOriginalCardUID: string = '';
  editCardSubmitting: boolean = false;
  editCardDialogRef: NbDialogRef<any> | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private service: ApiService,
    private toastr: ToastrService,
    private spinner: NgxSpinnerService,
    private location: Location,
    private dialogService: NbDialogService
  ) { }

  ngOnInit(): void {
    this.userId = this.route.snapshot.paramMap.get('id') || '';
    if (!this.userId) {
      this.error = 'No user ID provided';
      this.toastr.error(this.error);
      return;
    }

    this.loadUserInfo();
    this.loadUserCards();
    this.loadStores();
  }

  loadStores(): void {
    this.storesLoading = true;
    const role = localStorage.getItem('role');
    const managerId = localStorage.getItem('managerId');

    let endpoint: string;
    if (role === 'manager' && managerId) {
      endpoint = `${ApiUrl.storeListManagerDropdown}${managerId}`;
    } else {
      endpoint = ApiUrl.storeListAdminDropdown;
    }

    this.service.getRequest(endpoint).subscribe(
      (res: any) => {
        const responseData = res.response || res;
        if (responseData.success) {
          let storeList: any[] = [];
          if (role === 'manager') {
            storeList = responseData.data || [];
          } else {
            if (responseData.data?.storesData) {
              storeList = responseData.data.storesData;
            } else if (Array.isArray(responseData.data)) {
              storeList = responseData.data;
            }
          }
          this.stores = storeList.map((store: any) => {
            if (store.id && !store._id) store._id = store.id;
            if (!store.name && store.storeName) store.name = store.storeName;
            else if (!store.name) store.name = store._id;
            return store;
          });
        } else {
          this.stores = [];
        }
        this.storesLoading = false;
      },
      (err: any) => {
        this.stores = [];
        this.storesLoading = false;
      }
    );
  }

  // Load basic user info
  loadUserInfo(): void {
    this.spinner.show();
    this.service.getRequest(`${ApiUrl.getUserById}${this.userId}`).subscribe(
      (res: any) => {
        const responseData = res.response || res;
        if (responseData.success) {
          this.userName = responseData.data?.name || 'User';
        } else {
          this.error = responseData.message || 'Failed to load user data';
          this.toastr.error(this.error);
        }
        this.spinner.hide();
      },
      (err: any) => {
        this.error = err.error?.message || 'Failed to load user data';
        this.toastr.error(this.error);
        this.spinner.hide();
      }
    );
  }

  // Load all cards linked to the user
  loadUserCards(): void {
    this.loading = true;
    this.spinner.show();

    this.service.getRequest(`${ApiUrl.getUserCards}${this.userId}`).subscribe(
      (res: any) => {
        const responseData = res.response || res;

        if (responseData.success) {
          this.userCards = responseData.data || [];
          this.error = undefined;
        } else {
          this.userCards = [];
          this.error = responseData.message || 'Failed to load user cards';
          this.toastr.error(this.error);
        }

        this.loading = false;
        this.spinner.hide();
      },
      (err: any) => {
        this.userCards = [];
        this.error = err.error?.message || 'Failed to load user cards';
        this.toastr.error(this.error);
        this.loading = false;
        this.spinner.hide();
      }
    );
  }

  // Format timestamp for display
  formatTimestamp(timestamp: string): string {
    const date = new Date(timestamp);
    return date.toLocaleString();
  }

  // ---- Add Card Dialog ----

  openAddCardDialog(): void {
    this.newCardUID = '';
    this.newCardStoreId = '';
    this.addCardSubmitting = false;
    this.addCardDialogRef = this.dialogService.open(this.addCardTemplate);
  }

  submitAddCard(): void {
    const cardUID = normalizeCardUid(this.newCardUID);
    if (!cardUID || !this.newCardStoreId) {
      Swal.default.fire('Validation Error', 'Card UID and Store are required.', 'warning');
      return;
    }

    this.addCardSubmitting = true;
    const payload = {
      userId: this.userId,
      cardUID: cardUID,
      storeId: this.newCardStoreId,
      skipSignature: true
    };

    this.service.postRequest(ApiUrl.linkCardToUser, payload).subscribe(
      (res: any) => {
        const responseData = res.response || res;
        this.addCardSubmitting = false;
        if (responseData.success) {
          Swal.default.fire({
            title: 'Card Linked',
            text: 'Card UID linked successfully.',
            icon: 'success',
            timer: 2000,
            showConfirmButton: false
          });
          this.addCardDialogRef?.close();
          this.loadUserCards();
        } else {
          Swal.default.fire('Error', responseData.message || 'Failed to link card', 'error');
        }
      },
      (err: any) => {
        this.addCardSubmitting = false;
        Swal.default.fire(
          'Error',
          err.error?.response?.message || err.error?.message || 'Failed to link card',
          'error'
        );
      }
    );
  }

  // ---- Edit Card Dialog ----

  openEditCardDialog(card: any): void {
    this.editOriginalCardUID = card.cardUID;
    this.editCardUID = card.cardUID;
    this.editCardStoreId = card.storeId || '';
    this.editCardSubmitting = false;
    this.editCardDialogRef = this.dialogService.open(this.editCardTemplate);
  }

  submitEditCard(): void {
    const newCardUID = normalizeCardUid(this.editCardUID);
    if (!newCardUID || !this.editCardStoreId) {
      Swal.default.fire('Validation Error', 'Card UID and Store are required.', 'warning');
      return;
    }

    this.editCardSubmitting = true;
    const url = `${ApiUrl.getUserCards}${this.userId}/${this.editOriginalCardUID}`;
    const payload = {
      newCardUID: newCardUID,
      newStoreId: this.editCardStoreId
    };

    this.service.putRequest(url, payload).subscribe(
      (res: any) => {
        const responseData = res.response || res;
        this.editCardSubmitting = false;
        if (responseData.success) {
          Swal.default.fire({
            title: 'Card Updated',
            text: 'Card UID updated successfully.',
            icon: 'success',
            timer: 2000,
            showConfirmButton: false
          });
          this.editCardDialogRef?.close();
          this.loadUserCards();
        } else {
          Swal.default.fire('Error', responseData.message || 'Failed to update card', 'error');
        }
      },
      (err: any) => {
        this.editCardSubmitting = false;
        Swal.default.fire(
          'Error',
          err.error?.response?.message || err.error?.message || 'Failed to update card',
          'error'
        );
      }
    );
  }

  // ---- Delete Card ----

  deleteCard(cardId: string, cardUID: string): void {
    Swal.default.fire({
      title: 'Delete Card?',
      text: `Are you sure you want to remove card ${cardUID} from this user?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!'
    }).then((result) => {
      if (result.isConfirmed) {
        this.spinner.show();

        this.service.deletRequest(`${ApiUrl.getUserCards}${this.userId}/${cardUID}`).subscribe(
          (res: any) => {
            const responseData = res.response || res;

            if (responseData.success) {
              this.toastr.success('Card removed successfully');
              this.loadUserCards();
            } else {
              this.error = responseData.message || 'Failed to remove card';
              this.toastr.error(this.error);
              this.spinner.hide();
            }
          },
          (err: any) => {
            this.error = err.error?.message || 'Failed to remove card';
            this.toastr.error(this.error);
            this.spinner.hide();
          }
        );
      }
    });
  }

  // Navigate back to the previous page
  goBack(): void {
    this.location.back();
  }
}
